import { Alert, Paper, Typography } from "@mui/material";
import { capitalize } from "common/utils/strings";
import { FormikDimensionedNumberInput } from "common/components/FormikDimensionedNumberInput";
import { TractorBoundaryViewModel } from "./boundaryModel";
import { TypedPathTo, useFormikValuesAtBasePath } from "portal/utils/typedPath";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import React from "react";
import TractorDiagramDark from "portal/images/tractor-diagram-dark.svg?react";
import TractorDiagramLight from "portal/images/tractor-diagram-light.svg?react";

export interface TractorDimensionsViewModel {
  wheelbaseM: number;
  wheelSpacingM: number;
  boundaryViewModel: TractorBoundaryViewModel;
}

interface TractorDimensionsSubformProps {
  basePath: TypedPathTo<TractorDimensionsViewModel>;
  mode: "archetype" | "tractor" | "readonly";
}

export const TractorDimensionsSubform = ({
  basePath,
  mode,
}: TractorDimensionsSubformProps): JSX.Element => {
  const { t } = useTranslation();
  const { measurementSystem } = useSelf();
  const values = useFormikValuesAtBasePath(basePath);

  // leave the boundary model alone if it's a point-capture boundary model
  const boundaryModelEditable =
    values.boundaryViewModel.measuringTapeV1 !== undefined;

  return (
    <Paper className="grid grid-cols-[min-content_minmax(0,1fr)] gap-4 p-4">
      <Typography className="col-span-full pb-4 font-bold">
        {capitalize(
          t("views.autotractor.equipment.tractors.physicalDimensions")
        )}
      </Typography>
      {!boundaryModelEditable && (
        <Alert severity="warning" className="col-span-full">
          {t("views.autotractor.equipment.companionLockout")}
        </Alert>
      )}
      {/* two different tractor diagrams for the two color schemes */}
      <TractorDiagramDark className="hidden dark:block pl-4 w-[18rem]" />
      <TractorDiagramLight className="block dark:hidden pl-4 w-[18rem]" />
      {/* the actual form */}
      <div className="flex flex-col gap-2 pl-8 justify-center">
        <FormikDimensionedNumberInput
          name={
            basePath.boundaryViewModel.measuringTapeV1.yNoseToFrontAxleM.$path
          }
          // FIXME cqb: I don't like doing string concatenation here, but
          // juxtaposing a number callout with the baked-in label would require
          // refactoring everything down to NumberInput to take element-valued
          // labels instead of strings
          label={`\u2460 ${capitalize(
            t("views.autotractor.equipment.tractors.frontmostPointToFrontAxle")
          )}`}
          tProps={{ minimum: 0 }}
          dimensionedNumberInputProps={{
            minStoredValue: 0,
            storedUnit: "m",
            localSystem: measurementSystem,
            displayUnits: {
              imperial: "in",
              metric: "cm",
            },
            disabled: mode === "readonly" || !boundaryModelEditable,
          }}
        />
        <FormikDimensionedNumberInput
          name={basePath.wheelbaseM.$path}
          label={`\u2461 ${capitalize(t("models.equipment.wheelBase"))}`}
          tProps={{ minimum: 0 }}
          dimensionedNumberInputProps={{
            minStoredValue: 0,
            storedUnit: "m",
            localSystem: measurementSystem,
            displayUnits: {
              imperial: "in",
              metric: "cm",
            },
            disabled: mode !== "archetype" || !boundaryModelEditable,
          }}
          className=""
        />
        <FormikDimensionedNumberInput
          name={basePath.boundaryViewModel.measuringTapeV1.overallLengthM.$path}
          label={`\u2462 ${capitalize(
            t("views.autotractor.equipment.tractors.overallLength")
          )}`}
          tProps={{ minimum: 0 }}
          dimensionedNumberInputProps={{
            minStoredValue: 0,
            storedUnit: "m",
            localSystem: measurementSystem,
            displayUnits: {
              imperial: "in",
              metric: "cm",
            },
            disabled: mode === "readonly" || !boundaryModelEditable,
          }}
          className=""
        />
        <FormikDimensionedNumberInput
          name={basePath.wheelSpacingM.$path}
          label={`\u2463 ${capitalize(t("models.equipment.wheelSpacing"))}`}
          tProps={{ minimum: 0 }}
          dimensionedNumberInputProps={{
            minStoredValue: 0,
            storedUnit: "m",
            localSystem: measurementSystem,
            displayUnits: {
              imperial: "in",
              metric: "cm",
            },
            disabled: mode === "readonly" || !boundaryModelEditable,
          }}
          className=""
        />
        <FormikDimensionedNumberInput
          name={basePath.boundaryViewModel.measuringTapeV1.overallWidthM.$path}
          label={`\u2464 ${capitalize(
            t("views.autotractor.equipment.tractors.overallWidth")
          )}`}
          tProps={{ minimum: 0 }}
          dimensionedNumberInputProps={{
            minStoredValue: 0,
            storedUnit: "m",
            localSystem: measurementSystem,
            displayUnits: {
              imperial: "in",
              metric: "cm",
            },
            disabled: mode === "readonly" || !boundaryModelEditable,
          }}
          className=""
        />
      </div>
    </Paper>
  );
};
